import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import pluralize from 'pluralize'
import { useState } from 'react'
import { useArchiveCriteriaMetric } from '~/actions'
import { EditCriteriaForm } from '~/features/criteria-forms/edit-criteria-form'
import { RouterOutput } from '~/services/trpc'
import { useTRPC } from '~/services/trpc'
import {
  SettingsContent,
  SettingsPagination,
  useSettingTitle,
} from '~/templates/settings'

import {
  AlertDialog,
  AlertDialogTrigger,
  Button,
  Command,
  CommandItem,
  CommandList,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Heading1,
  Icon,
  Search,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@hap/components'

export const Route = createFileRoute('/_authenticated/$alias/settings/metrics/')({
  component: MetricsSettings,
})

function MetricsSettings() {
  const trpc = useTRPC()
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })

  const setQuery = (value: string | undefined) => {
    navigate({
      to: location.pathname,
      search: {
        ...(location.search as Record<string, unknown>),
        query: value,
        page: 1,
      },
      replace: true,
    })
  }

  const openScorecardCriteria = (scorecardId: string) => {
    navigate({
      to: '/$alias/settings/scorecards/$scorecardId/criteria',
      params: { scorecardId },
      search: location.search as never,
    })
  }

  const query = ((location.search as Record<string, unknown>).query as string) || ''
  const currentPage = ((location.search as Record<string, unknown>).page as number) ?? 1
  const currentPageIndex = currentPage - 1
  const pageSize = 30

  const { data } = useQuery(
    trpc.scorecardCriteria.search.queryOptions({
      query,
      pageNumber: currentPageIndex,
      pageSize: pageSize,
    }),
  )
  const metrics = data?.page
  const total = data?.total || null

  useSettingTitle([
    {
      label: 'Scorecard Metrics',
      description: 'A collection of metrics used in your scorecards.',
    },
  ])

  return (
    <SettingsContent spacing="sm" className="w-full overflow-y-auto">
      <header className="mb-10">
        <Heading1>Scorecard Metrics</Heading1>
        <p className="text-muted-foreground">
          A collection of metrics used in your scorecards.
        </p>
      </header>
      <div className="flex items-center gap-3">
        <Search
          placeholder="Search by name"
          onSearch={(value) => setQuery(value || undefined)}
          className="max-w-md"
          defaultValue={query}
        />
      </div>
      <div className="space-y-3">
        {metrics?.map((metric) => (
          <Metric key={metric.id} metric={metric} onNavigate={openScorecardCriteria} />
        ))}
        <SettingsPagination
          pageSize={pageSize}
          totalRecords={total}
          currentPage={currentPage}
        />
      </div>
    </SettingsContent>
  )
}

function Metric(props: {
  metric: RouterOutput['scorecardCriteria']['search']['page'][0]
  onNavigate: (scorecardId: string) => void
}) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [edit, setEdit] = useState(false)
  const { archiveMetric, isArchiving } = useArchiveCriteriaMetric()

  return (
    <div
      className={cn('bg-accent flex items-center justify-between rounded-md p-3', {
        'pointer-events-none opacity-20': isArchiving,
      })}
    >
      <h3 className="font-semibold">{props.metric.name}</h3>
      <div className="flex items-center gap-3">
        {props.metric.scorecards.length === 0 ? (
          <span className="flex text-yellow-400">
            <Icon name="warning-diamond" className="fill-yellow-400" />
            Not Used!
          </span>
        ) : (
          <Dialog>
            <Tooltip>
              <DialogTrigger asChild>
                <TooltipTrigger asChild>
                  <Button variant="ghost">
                    {props.metric.scorecards.length}{' '}
                    {pluralize('Scorecard', props.metric.scorecards.length)}
                  </Button>
                </TooltipTrigger>
              </DialogTrigger>
              <TooltipContent>Show scorecards</TooltipContent>
            </Tooltip>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Scorecards</DialogTitle>
              </DialogHeader>
              <DialogBody>
                <Command>
                  <CommandList>
                    {props.metric.scorecards.map((scorecard) => (
                      <div
                        className="border-b py-1 last:border-b-0"
                        key={scorecard.scorecardId}
                      >
                        <CommandItem
                          onSelect={() => props.onNavigate(scorecard.scorecardId)}
                          key={scorecard.scorecardId}
                          value={scorecard.scorecardId}
                        >
                          <div className="grow">{scorecard.scorecard.name}</div>
                          <div className="opacity-0 group-data-[selected=true]:opacity-100">
                            <Button variant="ghost" size="icon" icon="arrow-right" />
                          </div>
                        </CommandItem>
                      </div>
                    ))}
                  </CommandList>
                </Command>
              </DialogBody>
            </DialogContent>
          </Dialog>
        )}
        <DropdownMenu open={dialogOpen} onOpenChange={setDialogOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" icon="dots-three-outline" size="icon" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <Dialog open={edit} onOpenChange={setEdit}>
              <DialogTrigger asChild>
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  Edit Metric
                </DropdownMenuItem>
              </DialogTrigger>
              <DialogContent size="lg">
                <DialogHeader>
                  <DialogTitle>Edit {props.metric.name}</DialogTitle>
                </DialogHeader>

                <DialogBody>
                  <EditCriteriaForm criteriaId={props.metric.id} onOpenChange={setEdit} />
                </DialogBody>
              </DialogContent>
            </Dialog>

            <AlertDialog
              title={`Archive ${props.metric.name}`}
              description="Archiving this metric will cause it to be disappear from reports and scorecards."
              action={{
                value: 'Archive Metric',
                variant: 'destructive',
                onClick: async () => {
                  setDialogOpen(false)
                  await archiveMetric({ scorecardCriteriaId: props.metric.id })
                },
              }}
            >
              <AlertDialogTrigger asChild>
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  Archive Metric
                </DropdownMenuItem>
              </AlertDialogTrigger>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
